import React, { useState, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Grid, Typography, Box, InputAdornment, FormHelperText, CircularProgress } from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StyledTextField from "../../../../Inputs/StyledTextField/StyledTextField";
import PrimaryTextField from "../../../../Inputs/PrimaryTextField/PrimaryTextField";
import PrimaryButton from "../../../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../../../Buttons/SecondaryButton/SecondaryButton";
import TextStepper from "../../../../Steppers/TextStepper/TextStepper";
import PrimarySelect from "../../../../Inputs/PrimarySelect/PrimarySelect";
import useLoadStatus from "../../../../../hooks/useLoadStatus/useLoadStatus";
import CityAutocomplete from "../../../../Inputs/CityAutocomplete/CityAutocomplete";
//import PrimaryDateTimePicker from "../../../../Inputs/PrimaryDateTimePicker/PrimaryDateTimePicker";
//import DateTimeNowButton from "../../../../Buttons/DateTimeNowButton/DateTimeNowButton";
import WarningMessage from "../../../../WarningMessage/WarningMessage";
import { CarrierService } from "../../../../../services/CarrierService";
import { LoadService } from "../../../../../services/LoadService";
import { LocationService } from "../../../../../services/LocationService";
import { formatPhoneNumber } from "../../../../../utils/formatHelpers";
import { getETA } from "../../../../../utils/calcHelpers";
import { parseAndFormatDate, toISOLocal, changeTimeZone, getTimeZoneName } from "../../../../../utils/dateHelpers";
import { AlertContext } from "../../../../Dashboard/Dashboard";
import { isValid, isBefore, parseISO } from "date-fns";
import { handleAltEnter } from "../../../../../utils/keyboardShortcutHelpers";

export default function Callin({
  userId,
  activeLoad,
  setLoadStatus,
  setActiveLoad
}) {
  const todayString = new Date().toISOString();
  const { instance } = useMsal();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingLog, setIsLoadingLog] = useState(false);
  const [selectStatusCode, setSelectStatusCode] = useState("");
  const [statusCodes, setStatusCodes] = useState([]);
  const [callInStatusValid, setCallInStatusValid] = useState(false);
  const [selectETA, setSelectETA] = useState(parseAndFormatDate(todayString, "MM/dd/yy HH:mm"));
  const [timeIn, setTimeIn] = useState(null);
  const [timeOut, setTimeOut] = useState(null);
  const [comments, setComments] = useState("");
  const [callInByOrder, setCallInByOrder] = useState([]);
  const [selectTypeCode, setSelectTypeCode] = useState("");
  const [postDate, setPostDate] = useState(parseAndFormatDate(todayString, "MM/dd/yy HH:mm"));
  const [currentLocDefault, setCurrentLocDefault] = useState(null);
  const [currentLocation, setCurrentLocation] = useState({});
  const [nextLocDefault, setNextLocDefault] = useState("");
  const [carrier, setCarrier] = useState({});
  const [movement, setMovement] = useState({});
  const [miles, setMiles] = useState(null);
  const [currentLocValid, setCurrentLocValid] = useState(false);
  const [nextStop, setNextStop] = useState({});
  const [currentStop, setCurrentStop] = useState({});
  const [callInStatus, setCallInStatus] = useState({});
  const [commentsValid, setCommentsValid] = useState(false);
  const [localLoadStatus, setLocalLoadStatus] = useState(activeLoad?.brokerageStatus);
  const [previousTimeOut, setPreviousTimeOut] = useState(null);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [errorMessages, setErrorMessages] = useState([]);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [nextStopCity, setNextStopCity] = useState(null);
  //const [currentStopCity, setCurrentStopCity] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timeInValid, setTimeInValid] = useState(true);
  const [timeOutValid, setTimeOutValid] = useState(true);
  const { warning } = useContext(AlertContext);
  const LocalAllStatuses = useLoadStatus(localLoadStatus);
  const speed = 49.5;

  const errorMessageOptions = {
    departAfterArrive: "The actual departure cannot be before the actual arrival",
    arriveAfterPrevDepart: "The actual arrival cannot be before the last stop departure"
  };

  const addError = (message) => {
    if (!errorMessages.find(e => e === message)) {
      setErrorMessages(errorMessages => (
        [...errorMessages, message]
      ));
    }
  };

  const removeError = (message) => {
    if (errorMessages.find(e => e === message)) {
      setErrorMessages(errorMessages => (
        errorMessages.filter(e => e !== message)
      ));
    }
  };

  const fetchLog = () => {
    if (activeLoad.id) {
      setIsLoadingLog(true);
      setIsSubmitLoading(true);
      CarrierService.GetCallInsByOrder(instance, activeLoad.id).then(res => {
        setCallInByOrder([]);
        if (res !== null) {
          if (res.callins !== null) setCallInByOrder(res.callins);
        }
        setIsLoadingLog(false);
        setIsSubmitLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoadingLog(false);
        setIsSubmitLoading(false);
        warning(error);
      });
    }
  };

  const handleLoadRender = () => {
    setIsLoading(true);
    setIsSubmitLoading(true);
    LoadService.GetLoad(instance, activeLoad.id).then(res => {
      setActiveLoad(res);
      setIsLoading(false);
      setIsSubmitLoading(false);
    }).catch(error => {
      console.error(error);
      setIsLoading(false);
      setIsSubmitLoading(false);
      warning(error);
    });
  };

  const handleStatusSelect = (value) => {
    const objCallInStatus = statusCodes.find(s => s.callin_status === value);
    setCallInStatus(objCallInStatus);
    switch (objCallInStatus?.initiated_type) {
      case "B":
        setSelectTypeCode("Broker initiated");
        break;
      case "T":
        setSelectTypeCode("Brokerage tracking");
        break;
      default:
        setSelectTypeCode("");
    }

    value === "" ? setCallInStatusValid(false) : setCallInStatusValid(true);
    setSelectStatusCode(value);

  };

  const handleSubmit = () => {
    setIsLoading(true);
    setIsSubmitLoading(true);
    const callDateTime = toISOLocal(changeTimeZone(new Date(), "America/Chicago"));

    let callIn = {
      __type: "callin",
      company_id: currentLocation.company_id,
      call_date_time: callDateTime,
      city_id: currentLocation.id,
      city_name: currentLocation.name,
      latitude: currentLocation.latitude,
      longitude: currentLocation.longitude,
      current_stop_id: currentStop.id,
      entered_by: userId,
      miles2consignee: parseInt(miles),
      movement_id: activeLoad.movementId,
      movement_status: activeLoad.movement[0].status,
      new_eta: toISOLocal(new Date(selectETA)),
      order_id: activeLoad.id,
      payee_id: activeLoad.payeeId,
      state: currentLocation.state,
      zip_code: currentLocation.zip_code,
      initiated_type: callInStatus?.initiated_type,
      callin_status: selectStatusCode,
      next_stop_state: nextStop?.state,
      next_stop_city_id: nextStop?.city_id,
      next_stop_city_name: nextStop?.city_name,
      next_stop_zip_code: nextStop?.zip_code,
      remark: comments,
      miles2consignee_um: "MI",
      next_stop_avg_speed: speed,
      next_stop_time_left: parseInt(timeRemaining)
    };

    CarrierService.CreateCallIn(instance, callIn).then(() => {
      if (callInStatus.callin_status === "DISPATCH") handleUpdateToDISStatus();
      handleClear();
      handleLoadRender();
      fetchLog();
    }).catch(error => {
      console.error(error);
      setIsLoading(false);
      setIsSubmitLoading(false);
      warning(error);
    });
  };

  //const handleStopSubmit = () => {
  //  setIsLoading(true);
  //  setIsSubmitLoading(true);
  //  const stop = {
  //    stopId: currentStop.id,
  //    actualArrival: toISOLocal(timeIn),
  //    actualDeparture: toISOLocal(timeOut)
  //  };

  //  CarrierService.UpdatesStopsTimes(instance, stop).then(() => {
  //    handleClear();
  //    handleLoadRender();
  //    fetchLog();
  //  }).catch(error => {
  //    console.error(error);
  //    setIsLoading(false);
  //    setIsSubmitLoading(false);
  //    warning(error);
  //  });
  //};

  const handleUpdateToDISStatus = () => {
    setIsLoading(true);
    setIsSubmitLoading(true);

    CarrierService.UpdateToDISStatus(instance, activeLoad.movementId).then(() => {
      setIsLoading(false);
      setIsSubmitLoading(false);
    }).catch(error => {
      console.error(error);
      setIsLoading(false);
      setIsSubmitLoading(false);
      warning(error);
    });
  };

  const milesToNextStop = (latitude, longitude, stopId) => {
    CarrierService.GetMilesToNextStop(instance, latitude, longitude, stopId).then((res) => {
      const now = changeTimeZone(new Date(), getTimeZoneName(nextStopCity?.timezone_id));
      const miles = res.miles;
      const ETA = getETA(now, miles, speed);
      setSelectETA(ETA.newETA);
      setTimeRemaining(ETA.minutesRemaining);
      setMiles(res.miles);
    }).catch(error => {
      console.error(error);
      warning(error);
    });
  };

  const getNextStopCity = (city_name, zip_code, state) => {
    const searchParams = {
      name: city_name,
      zip: zip_code,
      state: state
    };

    LocationService.FindCities(instance, searchParams).then(res => {
      setNextStopCity(res[0]);
    }).catch(error => {
      console.error(error);
      warning(error);
    });
  };

  //const getCurrentStopCity = (city_name, zip_code, state) => {
  //  const searchParams = {
  //    name: city_name,
  //    zip: zip_code,
  //    state: state
  //  };

  //  LocationService.FindCities(instance, searchParams).then(res => {
  //    setCurrentStopCity(res[0]);
  //  }).catch(error => {
  //    console.error(error);
  //    warning(error);
  //  });
  //};

  const handleLoadTimeIn = (value) => {
    if (value) {
      setTimeIn(parseISO(value));
    }
  };

  const handleLoadTimeOut = (value) => {
    if (value) {
      setTimeOut(parseISO(value));
    }
  };

  const handleClear = () => {
    setSelectStatusCode("");
    setCallInStatusValid(false);
    setSelectETA(parseAndFormatDate(todayString, "MM/dd/yy HH:mm"));
    setComments("");
    setSelectTypeCode("");
    setPostDate(parseAndFormatDate(todayString, "MM/dd/yy HH:mm"));
    setCurrentLocDefault("");
    setNextLocDefault("");
    setMiles(null);
    setCurrentLocValid(false);
    setCurrentLocation({});
    setCommentsValid(false);
    setTimeIn(null);
    setTimeOut(null);
  };

  const handleCancel = () => {
    handleClear();
    window.close();
  };

  const handleCityChange = (c) => {
    if (c) {
      setCurrentLocation(c);
      setCurrentLocValid(true);
      setNextLocDefault(`${nextStop?.city_name}, ${nextStop?.state}`);
      milesToNextStop(c.latitude, c.longitude, nextStop?.id);
    } else {
      setCurrentLocation({});
      setMiles(null);
      setCurrentLocValid(false);
      setNextLocDefault("");
      setSelectETA(parseAndFormatDate(todayString, "MM/dd/yy HH:mm"));
    }
  };

  useEffect(() => {
    setCommentsValid(!!comments);
  }, [comments]);

  useEffect(() => {
    setSubmitDisabled(!currentLocValid || !callInStatusValid || !commentsValid || !timeInValid || !timeOutValid);
  }, [currentLocValid, callInStatusValid, commentsValid, timeInValid, timeOutValid]);

  useEffect(() => {
    if (timeIn && !isValid(timeIn)) {
      setTimeInValid(false);
      removeError(errorMessageOptions.arriveAfterPrevDepart);
    }
    else if (timeIn && previousTimeOut && isBefore(timeIn, previousTimeOut)) {
      setTimeInValid(false);
      addError(errorMessageOptions.arriveAfterPrevDepart);
    } else {
      setTimeInValid(true);
      removeError(errorMessageOptions.arriveAfterPrevDepart);
    }

    if (timeOut && !isValid(timeOut)) {
      setTimeOutValid(false);
      removeError(errorMessageOptions.departAfterArrive);
    } else if (timeOut && timeIn && isBefore(timeOut, timeIn)) {
      setTimeOutValid(false);
      addError(errorMessageOptions.departAfterArrive);
    } else {
      setTimeOutValid(true);
      removeError(errorMessageOptions.departAfterArrive);
    }
  }, [timeIn, timeOut]);

  useEffect(() => {
    setIsLoading(true);
    setIsSubmitLoading(true);
    CarrierService.GetCallInStatuses(instance).then(res => {
      setStatusCodes(res.callinStatus);
      setIsLoading(false);
      setIsSubmitLoading(false);
    }).catch(error => {
      console.error(error);
      setIsLoading(false);
      setIsSubmitLoading(false);
      warning(error);
    });

    fetchLog();

    if (activeLoad.brokerageStatus) { setLocalLoadStatus(activeLoad.brokerageStatus); setLoadStatus(activeLoad.brokerageStatus); }

    if (activeLoad.movements) {
      setMovement(activeLoad.movements[0]);
    }

    if (activeLoad.stops?.length) {
      const stops = activeLoad.stops;
      let prevTimeOut = null;
      let currentS = stops.find(s => (s.actual_arrival === null && s.actual_departure === null) || s.actual_departure === null);
      if (currentS === undefined) { currentS = stops[stops.length - 1]; }
      let nextS = stops.find(s => s.actual_arrival === null);
      if (nextS === undefined) { nextS = stops[stops.length - 1]; }
      const timeIn = currentS.actual_arrival !== null ? currentS.actualArrival : null;
      const timeOut = currentS.actual_departure !== null ? currentS.actualDeparture : null;
      prevTimeOut = currentS.order_sequence > 1 ? parseISO(stops[currentS.order_sequence - 2].actualDeparture) : null;

      setCurrentStop(currentS);
      setNextStop(nextS);
      setPreviousTimeOut(prevTimeOut);
      handleLoadTimeIn(timeIn);
      handleLoadTimeOut(timeOut);
      getNextStopCity(nextS.city_name, nextS.zip_code, nextS.state);
      //getCurrentStopCity(currentS.city_name, currentS.zip_code, currentS.state);
    }

    if (activeLoad.payeeId) {
      CarrierService.GetCarrier(instance, activeLoad.payeeId).then(res => {
        setCarrier(res);
        setIsLoading(false);
        setIsSubmitLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        setIsSubmitLoading(false);
        warning(error);
      });
    }
  }, [activeLoad]);

  return (
    <Box onKeyDown={(e) => handleAltEnter(e, submitDisabled, handleSubmit)}>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <CircularProgress sx={{ color: "primary.60" }} size={40} />
        </Box>
      ) : (
        <Box>
          {isLoadingLog ? (
            <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
              <CircularProgress sx={{ color: "primary.60" }} size={40} />
            </Box>
          ) : (callInByOrder.length > 0 && <TextStepper callInByOrder={callInByOrder} currentStatus={LocalAllStatuses.currentStatus?.name} />)}
          <Grid container spacing={2}>
            <Grid item>
              <Box className="Callin-modal-form-row" sx={{ alignItems: "center" }}>
                <Typography variant="subhead2" color="black_solid.60" sx={{ padding: "0 0 15px 0" }}>
                  Brokerage Status
                </Typography>
                <Typography variant="h3" color="primary.60" sx={{ padding: "0 0 12px 0" }}>
                  {LocalAllStatuses.currentStatus?.name?.toUpperCase()}
                </Typography>
              </Box>
              <Box className="Callin-modal-form-row" sx={{ alignItems: "center" }}>
                <PrimarySelect
                  id={"callin-status-select"}
                  label="Call in Status *"
                  value={selectStatusCode}
                  onChange={o => handleStatusSelect(o)}
                  options={statusCodes.map((o) => (o))}
                  flexGrow={1}
                  displayEmpty
                  width={"390px"}
                  nameKey="descr"
                  valueKey="callin_status"
                  error={!callInStatusValid}
                />
              </Box>
              <Box className="Callin-modal-form-row" sx={{ alignItems: "center" }}>
                <FormHelperText sx={{ fontSize: "subhead2.fontSize", color: "black_solid.80" }}>
                  {<>Contact for <strong>{carrier.name}</strong></>}
                </FormHelperText>
                <StyledTextField
                  id="contact-information-input"
                  sx={{ width: "390px" }}
                  InputProps={{
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <LocalShippingIcon sx={{ fontSize: "20px" }} />
                        </InputAdornment>
                        <InputAdornment sx={{ margin: "0 0 0 6px", }} position="start">
                          <Typography variant="body1" color="black_solid.60">
                            {movement.driverName}
                          </Typography>
                        </InputAdornment>
                      </>
                    ),
                    sx: { fontSize: "subhead2.fontSize", color: "primary.60" },
                    inputProps: {
                      style: { textAlign: "center", padding: "4px 8px" },
                    }
                  }}
                  variant="outlined"
                  value={formatPhoneNumber(carrier.phoneNumber) || ""}
                />
              </Box>
              <Box className="Callin-modal-form-row" sx={{ alignItems: "center" }}>
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                  <PrimaryTextField
                    id="callin-eta-to-next-stop"
                    label="ETA to Next Stop"
                    value={parseAndFormatDate(selectETA, "MM/dd/yy HH:mm")}
                    onChange={setSelectETA}
                    fullWidth
                    disabled
                  />
                  <Box sx={{ marginLeft: "20px" }}>
                    <PrimaryTextField
                      id="callin-distance-to-next-stop"
                      value={miles ? `${miles} miles` : ""}
                      fullWidth
                      onChange={() => { }}
                      label="Distance to Next Stop"
                      disabled
                    />
                  </Box>
                </Box>
              </Box>
              <Box className="Callin-modal-form-row" sx={{ alignItems: "center" }}>
                <Box>
                  <PrimaryTextField
                    id="callin-comments"
                    label="Comments *"
                    value={comments}
                    onChange={setComments}
                    width="390px"
                    placeholder="Enter comments here"
                    multiline
                    rows={6}
                    error={!commentsValid}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Box className="Callin-modal-form-row" sx={{ alignItems: "center" }}>
                <PrimaryTextField
                  id="callin-Call-information-input"
                  label="Callin Type"
                  value={selectTypeCode}
                  width="390px"
                  onChange={() => { }}
                  disabled
                />
              </Box>
              <Box className="Callin-modal-form-row" sx={{ alignItems: "center" }}>
                <PrimaryTextField
                  id="callin-movement"
                  label="Movement"
                  value={activeLoad.movementId || ""}
                  onChange={() => { }}
                  width="390px"
                  disabled
                />
              </Box>
              <Box className="Callin-modal-form-row" sx={{ alignItems: "center" }}>
                <CityAutocomplete
                  updateSelectedCity={handleCityChange}
                  label="Current Location *"
                  defaultInput={currentLocDefault}
                  disableCounterIncrement
                  disableAutoValue
                  error={!currentLocValid}
                  width="390px"
                />
              </Box>
              <Box className="Callin-modal-form-row" sx={{ alignItems: "center", padding: "3px 0 0 0" }}>
                <PrimaryTextField
                  id="callin-next-stop"
                  label="Next Stop"
                  value={nextLocDefault || ""}
                  onChange={setNextLocDefault}
                  width="390px"
                  disabled
                />
              </Box>
              <Box className="Callin-modal-form-row" sx={{ alignItems: "center" }}>
                <Grid container columnSpacing={{ xs: 1 }}>
                  <Grid item xs={6}>
                    <PrimaryTextField
                      id="callin-stop"
                      label={`Stop ${currentStop?.order_sequence}`}
                      value={`${currentStop?.city_name}, ${currentStop?.state}`}
                      onChange={o => o}
                      width="190px"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <PrimaryTextField
                      id="callin-post-date"
                      label="Post Date"
                      value={postDate || ""}
                      onChange={setPostDate}
                      width="190px"
                      disabled
                    />
                  </Grid>
                </Grid>
              </Box>
              {/* <Box className="Callin-modal-form-row" sx={{ alignItems: "center" }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="subhead2" color="black_solid.60" sx={{ padding: "0 0 9px 0" }}>
                      Time In
                    </Typography>
                    <Box sx={{ display: "flex" }}>
                      <PrimaryDateTimePicker
                        value={timeIn}
                        handleChange={setTimeIn}
                        width="178px"
                        error={!timeInValid}
                        ampm={false}
                      />
                      <DateTimeNowButton onClick={() => setTimeIn(changeTimeZone(new Date(), getTimeZoneName(currentStopCity?.timezone_id)))} />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subhead2" color="black_solid.60" sx={{ padding: "0 0 9px 0" }}>
                      Time Out
                    </Typography>
                    <Box sx={{ display: "flex" }}>
                      <PrimaryDateTimePicker
                        value={timeOut}
                        handleChange={setTimeOut}
                        width="178px"
                        error={!timeOutValid}
                        ampm={false}
                      />
                      <DateTimeNowButton onClick={() => setTimeOut(changeTimeZone(new Date(), getTimeZoneName(currentStopCity?.timezone_id)))} />
                    </Box>
                  </Grid>
                </Grid>
              </Box> */}
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", marginTop: "9px", alignItems: "center", maxWidth: "970px" }}>
            <Box>
              {errorMessages.map((error) => (
                <WarningMessage key={error} text={error} displayText={error} />
              ))}
            </Box>
            <Box sx={{ margin: "0 20px 0 auto" }}>
              <SecondaryButton
                label="Cancel"
                onClick={handleCancel}
              />
            </Box>
            <PrimaryButton
              label="Submit"
              onClick={handleSubmit}
              isLoading={isSubmitLoading}
              disabled={submitDisabled}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

Callin.propTypes = {
  userId: PropTypes.string,
  activeLoad: PropTypes.object.isRequired,
  setLoadStatus: PropTypes.func,
  setActiveLoad: PropTypes.func
};